/* =========================================================================
 * THE OUTREACH SEQUENCE - a handful of angles, generic enough to fit every
 * kind of account, specific because the FACTS come from the record.
 *
 * Brian, 2026-08-25: "Each account has its own angle. You can review and
 * create a handful of angles that will be generic enough to fit."
 *
 * WHAT WAS WRONG WITH THE FIRST CUT. The six angles were school-shaped:
 * "at their school", "every spring", "the banquet", "the player wearing it".
 * That reads correctly for a 3A volleyball program and badly for the Winnipeg
 * Sea Bears, a police department, a Greek chapter or a corporate account -
 * and this universe is all of those. Measured on the live filter lists: 40
 * distinct levels, of which 19 are high-school classifications but the rest
 * span college, pro, league, open, conference, regional and world - with
 * separate Command Center spaces for Corporate, Police, Fire, Government,
 * Military and Greek on top.
 *
 * THE RESOLUTION, and it is the whole design: SPECIFICITY COMES FROM THE
 * FACTS, GENERALITY COMES FROM THE FRAMING. "Congratulations on the 3-2 win
 * over Cardinal Gibbons at Koka Booth" is specific because of the score, the
 * opponent and the venue - not because the sentence around them mentions a
 * school. So the framing says "the group", "everyone who earned one", "the
 * date you want them in hands", and the record supplies what makes it theirs.
 *
 * That is why there are six angles and not sixty. An angle per account type
 * would be a maintenance problem and would still miss the next type. Six
 * angles that each rest on something universally true of a recognition sale
 * cover all of them:
 *
 *   1. they won a specific thing        2. the ring can be shown, not described
 *   3. each person gets their own       4. there is a date it has to exist by
 *   5. they already have a vendor       6. this repeats next year
 *
 * THE REFUSAL RULE, inherited from the placeholder-copy incident: a variant
 * whose facts are missing is UNAVAILABLE and names the fact it needs. It is
 * never composed with a gap, a hedge, or a "[score here]".
 * ========================================================================= */

import { titleTierFor } from './titleTierView.js'
import { MISSING_CHAMPION_LABEL } from './championToClose.js'

const S = (v) => (v == null ? '' : String(v).trim())

/** Organization identity is constant; the signature is not a generic brand.
 * Each new preview requires the authenticated current representative's name.
 * The backend independently re-resolves that person before a draft operation.
 * This composer never updates an existing saved draft. */
const SR_BRAND = Object.freeze({ name: 'Southern Recognition', short: 'SR' })

export function brandFor() {
  return SR_BRAND
}

/** Pull the facts once so every composer reads the same values. */
export function factsFrom(dossier) {
  const c = dossier?.championship || {}
  const st = c.story || {}
  const champ = dossier?.champion || {}
  const coach = dossier?.coach || {}
  return {
    school: S(champ.school),          // the winning entity, whatever kind it is
    mascot: S(champ.mascot),
    colors: (champ.colors || []).map((x) => S(x.label) || S(x.hex)).filter(Boolean),
    sport: S(c.sport),
    level: S(c.level),
    season: S(c.season),
    state: S(c.state),
    conference: S(c.conference),
    dateLabel: c.dateCustomerSafe === true ? S(c.effDateLabel) : '',
    incumbent: S(c.incumbent),
    titleLine: S(st.titleLine),
    storyText: S(st.text),
    score: S(st.score),
    runnerUp: S(st.runnerUp),
    record: S(st.record),
    venue: S(st.venue),
    rosterUrl: S(st.rosterUrl),
    coachName: S(coach.name),
    coachEmail: S(coach.email),
    sourceUrl: S(c.championSourceUrl),
  }
}

/* ---- ADDRESSING SOMEONE WITHOUT GUESSING WHAT THEY ARE ------------------
 * The name we hold comes from `champion_head_coach`, so when it is present
 * "Coach" is a fact about the field, not an assumption about the account.
 * When it is absent we do NOT invent a title - a police chief, an AD and a
 * chapter president are all wrong to call Coach, and getting someone's title
 * wrong in the first line is worse than not using one.
 * ---------------------------------------------------------------------- */
const greet = (f) => {
  if (!f.coachName) return ''
  const last = f.coachName.split(/\s+/).filter(Boolean).pop()
  return `Coach ${last},`
}

const signOff = (f) => `${f.senderName}\n${brandFor(f.state).name}`

// Normalize generated preview before the human approves it; never rewrite a saved draft.
export const normalizeOutreachCopy = value => String(value ?? '').replace(/\u2014/g, ' - ').replace(/[ \t]+/g, ' ').split('\n').map(line => line.trim()).join('\n').trim()

/* ---- SAYING THE TITLE WITHOUT A BROKEN ARTICLE -------------------------
 * "the NCHSAA 3A Volleyball State Championship" is right.
 * "the Officer of the Year" is not - it reads like a typo, and this universe
 * holds plenty of award-shaped titles alongside competition-shaped ones
 * (Officer of the Year, Community Hero, Letterwinner recognitions).
 *
 * The distinction is in the title itself, so we read it rather than guess
 * from the account: competition words take an article, awards do not.
 * ---------------------------------------------------------------------- */
const COMPETITION = /\b(championship|championships|cup|final|finals|title|tournament|series|bowl|classic|invitational|nationals|worlds|open|playoffs|meet|games)\b/i
export function titlePhrase(titleLine) {
  const t = S(titleLine)
  if (!t) return ''
  if (/^the\s/i.test(t)) return t
  return COMPETITION.test(t) ? `the ${t}` : t
}

/** Use sourced result details without claiming attendance or observation. */
function proofLine(f) {
  const result=f.score&&f.runnerUp?`with a ${f.score} win over ${f.runnerUp}`:f.runnerUp?`with a win over ${f.runnerUp}`:f.score?`with a ${f.score} win`:''
  return [result,f.venue?`at ${f.venue}`:''].filter(Boolean).join(' ')
}

/** Join lines, collapsing the blanks left by facts we did not have. */
const body = (lines) => lines.filter((l, i) => l !== '' || lines[i - 1] !== '').join('\n').trim()

/* -------------------------------------------------------------------------
 * THE SIX ANGLES
 *
 * Each declares what it NEEDS. `need` returns the missing facts in plain
 * language - the same words the panel shows the rep, so "why can't I send
 * this" and "what is missing" are never two different answers.
 * ---------------------------------------------------------------------- */
export const OUTREACH_STYLES = [
  {
    key: 'congratulations',
    label: 'The congratulation',
    persona: 'Whoever led them',
    day: 0,
    angle: 'Recognizes the verified achievement and asks the recipient to share the congratulations with the group.',
    why: 'Recognition stands on its own. A reply adds the recipient perspective without creating a sales enrollment.',
    need: (f) => {
      const m = []
      if (!f.school) m.push(MISSING_CHAMPION_LABEL)
      if (!f.titleLine) m.push('the exact title wording')
      return m
    },
    compose: (f) => {
      const proof = proofLine(f)
      return {
        subject: `Congratulations, ${f.school} - ${f.titleLine}`,
        body: body([
          greet(f),
          '',
          `Congratulations to ${f.school} on ${titlePhrase(f.titleLine)}${f.season&&!f.titleLine.includes(f.season)?` in ${f.season}`:''}${proof ? `, ${proof}` : ''}.`,
          '',
          f.sport ? `Would you pass our congratulations along to the ${f.school} ${f.sport} athletes and coaches?` : `Would you share our congratulations with everyone at ${f.school} who contributed to this achievement?`,
          '',
          signOff(f),
        ]),
      }
    },
  },

  {
    key: 'the_ring',
    label: 'The ring concept',
    persona: 'Whoever led them',
    day: 3,
    angle: 'Leads with the render in their own colors and marks. Shows instead of offers.',
    why: 'Nobody can picture a ring from a paragraph. They can react to a picture instantly, and a reaction is a reply.',
    need: (f, ctx) => {
      const m = []
      if (!f.school) m.push(MISSING_CHAMPION_LABEL)
      // `approvedSurfaces` is the ATTACHABLE count (approved AND an owned mark),
      // so a proof with a model-drawn crest can never make this email say
      // "surfaces attached".
      if (!ctx?.approvedSurfaces) m.push('a saved artwork file selected for attachment')
      return m
    },
    compose: (f, ctx) => {
      const n = ctx.approvedSurfaces
      return {
        subject: `${f.school} - ring artwork`,
        body: body([
          greet(f),
          '',
          f.titleLine ? `Here is the ring concept for ${f.school}, created to mark ${titlePhrase(f.titleLine)} and give the group a lasting reminder of what they earned.` : `This ring concept is for ${f.school}.`,
          '',
          n === 1 ? `The concept artwork is attached. Are you available for a 15-minute review of the ${f.school} design next week?` : `${n} artwork concepts are attached. Which ${f.school} concept would you like to review together?`,
          '',
          signOff(f),
        ]),
      }
    },
  },

  {key:"every_name",label:"Personal design details",persona:'Current account contact',day:7,angle:"The selected concept and the actual design-sharing and response history are needed before a useful personal-detail follow-up can be written.",why:'Awaiting authoritative conversation evidence.',need:()=>["The selected concept and the actual design-sharing and response history are needed before a useful personal-detail follow-up can be written."],compose:()=>({subject:'',body:''})},
  {key:"the_date",label:"Presentation planning",persona:'Current account contact',day:12,angle:"The actual design conversation and current customer presentation plans are needed before a presentation follow-up can be written.",why:'Awaiting authoritative conversation evidence.',need:()=>["The actual design conversation and current customer presentation plans are needed before a presentation follow-up can be written."],compose:()=>({subject:'',body:''})},
  {key:"displacement",label:"Design comparison",persona:'Current account contact',day:18,angle:"A current sourced incumbent and the actual comparison request are needed before a design-comparison follow-up can be written.",why:'Awaiting authoritative conversation evidence.',need:()=>["A current sourced incumbent and the actual comparison request are needed before a design-comparison follow-up can be written."],compose:()=>({subject:'',body:''})},
  {key:"the_standard",label:"Further recognition",persona:'Current account contact',day:21,angle:"A relevant prior order and current expansion conversation are needed before another recognition occasion can be proposed.",why:'Awaiting authoritative conversation evidence.',need:()=>["A relevant prior order and current expansion conversation are needed before another recognition occasion can be proposed."],compose:()=>({subject:'',body:''})},
]

/**
 * Build the full sequence for a dossier.
 *
 * `ctx.approvedSurfaces` is the count of approved ring surfaces, because one
 * variant genuinely depends on having something to attach - the availability
 * check has to know about state outside the record.
 *
 * Returns every style, available or not. A rep should SEE the variant they
 * cannot send yet and what it needs, rather than have it silently vanish - a
 * missing option looks like a missing feature, and the point is to make the
 * gap actionable.
 */
export function buildSequence(dossier, ctx = {}) {
  const f = { ...factsFrom(dossier), senderName: normalizeOutreachCopy(ctx.sender?.name) }
  const routing = titleTierFor(dossier?.championship)
  const styles = routing.titleTier === 'congrats_tier'
    ? OUTREACH_STYLES.filter(style => style.key === 'congratulations')
    : OUTREACH_STYLES
  return styles.map((style) => {
    const missing = routing.titleTier === 'review_required'
      ? [routing.titleTierReason]
      : style.need(f, ctx) || []
    if (!ctx.sender?.id || !f.senderName || /[\r\n]/.test(String(ctx.sender?.name || ''))) missing.push('the signed-in representative’s current name')
    const available = missing.length === 0
    let subject = ''
    let text = ''
    if (available) {
      const out = style.compose(f, ctx)
      subject = normalizeOutreachCopy(out.subject)
      text = normalizeOutreachCopy(out.body)
    }
    return {
      key: style.key,
      label: style.label,
      persona: style.persona,
      day: style.day,
      angle: style.angle,
      why: routing.titleTier === 'congrats_tier'
        ? 'Recognize the regional or district achievement without a ring pitch or artwork attachment.'
        : style.why,
      available,
      stageBlocker: ['every_name', 'the_date', 'displacement', 'the_standard'].includes(style.key)
        ? 'The current composer does not supply the prior conversation and sharing evidence for this touch. A prepared draft or elapsed day is not a customer action.'
        : '',
      missing,
      subject,
      body: text,
    }
  })
}

/** How many of the sequence can actually go out right now. */
export function sequenceReadiness(seq) {
  const ready = seq.filter((s) => s.available && !s.stageBlocker).length
  return { ready, total: seq.length, blocked: seq.length - ready }
}

/** Human label for the send day. Day 0 is "send now", not "Day 0". */
export function dayLabel(day) {
  if (day === 0) return 'Send now'
  return `Day ${day}`
}
